export const IDLE_GAP_MINUTES = 60;
export const MAX_SEGMENT_HOURS = 24;
export const CLOCK_SKEW_MINUTES = 5;

function parseTimestamp(value, path) {
  const date = value instanceof Date ? new Date(value) : new Date(String(value || ""));
  if (Number.isNaN(date.getTime())) throw new TypeError(`${path} must be a valid timestamp`);
  return date;
}

function serialize(segment) {
  return [segment[0].toISOString(), segment[1].toISOString()];
}

export function mergeIntervals(intervals = []) {
  const sorted = intervals
    .map((segment) => [new Date(segment[0]), new Date(segment[1])])
    .sort((left, right) => left[0] - right[0] || left[1] - right[1]);
  const merged = [];
  sorted.forEach((segment) => {
    const previous = merged[merged.length - 1];
    if (!previous || segment[0].getTime() > previous[1].getTime()) {
      merged.push(segment);
    } else if (segment[1] > previous[1]) {
      previous[1] = segment[1];
    }
  });
  return merged;
}

export function cardioIntervalsFromItems(items = [], clientStartedAt) {
  let executionStart = null;
  return items.flatMap((item, index) => {
    const cardio = item?.cardio;
    if (!cardio?.completed || !Number.isInteger(cardio.minutes) || cardio.minutes < 1) return [];
    executionStart ||= parseTimestamp(clientStartedAt, "clientStartedAt");
    const end = parseTimestamp(cardio.completedAt, `items[${index}].cardio.completedAt`);
    const realStart = new Date(end.getTime() - cardio.minutes * 60 * 1000);
    const creditedStart = new Date(Math.max(realStart.getTime(), executionStart.getTime()));
    return end.getTime() > creditedStart.getTime() ? [[creditedStart, end]] : [];
  });
}

export function durationSecondsFromSegments(segments = []) {
  return Math.floor(
    segments.reduce((total, segment) => {
      const left = new Date(segment?.[0]).getTime();
      const right = new Date(segment?.[1]).getTime();
      return Number.isFinite(left) && Number.isFinite(right) && right >= left
        ? total + right - left
        : total;
    }, 0) / 1000
  );
}

export function effectiveActiveSegments({
  activeSegments = [],
  clientStartedAt,
  items = [],
  mergeCardio = true,
} = {}) {
  const cardio = mergeCardio ? cardioIntervalsFromItems(items, clientStartedAt) : [];
  return mergeIntervals([...activeSegments, ...cardio]).map(serialize);
}

export function normalizeActiveDuration({
  activeSegments = [],
  clientStartedAt,
  receivedAt,
  items = [],
  mergeCardio = true,
} = {}) {
  const start = parseTimestamp(clientStartedAt, "clientStartedAt");
  const received = parseTimestamp(receivedAt, "receivedAt");
  const windowStart = start.getTime() - CLOCK_SKEW_MINUTES * 60 * 1000;
  const windowEnd = received.getTime() + CLOCK_SKEW_MINUTES * 60 * 1000;
  let previousEnd = null;
  const validated = activeSegments.map((segment, index) => {
    if (!Array.isArray(segment) || segment.length !== 2) {
      throw new TypeError(`activeSegments[${index}] must be a pair`);
    }
    const left = parseTimestamp(segment[0], `activeSegments[${index}][0]`);
    const right = parseTimestamp(segment[1], `activeSegments[${index}][1]`);
    if (left > right || right - left > MAX_SEGMENT_HOURS * 60 * 60 * 1000 ||
      left.getTime() < windowStart || right.getTime() > windowEnd ||
      (previousEnd && left < previousEnd)) {
      throw new TypeError(`activeSegments[${index}] is outside the canonical bounds`);
    }
    previousEnd = right;
    return [left, right];
  });
  if (validated.length > 200) throw new TypeError("activeSegments cannot exceed 200 entries");
  const base = mergeIntervals(validated);
  const effective = mergeIntervals([
    ...base,
    ...(mergeCardio ? cardioIntervalsFromItems(items, start) : []),
  ]);
  effective.forEach(([left, right], index) => {
    if (left.getTime() < windowStart || right.getTime() > windowEnd ||
      right.getTime() - left.getTime() > MAX_SEGMENT_HOURS * 60 * 60 * 1000) {
      throw new TypeError(`activeSegments[${index}] is outside the canonical bounds`);
    }
  });
  return {
    activeSegments: base.map(serialize),
    effectiveActiveSegments: effective.map(serialize),
    activeDurationSeconds: durationSecondsFromSegments(effective),
  };
}

export function appendActivityEvent({ activeSegments = [], lastActivityAt, eventAt } = {}) {
  const event = parseTimestamp(eventAt, "eventAt");
  const previous = lastActivityAt ? parseTimestamp(lastActivityAt, "lastActivityAt") : null;
  const segments = activeSegments.map((segment, index) => [
    parseTimestamp(segment[0], `activeSegments[${index}][0]`),
    parseTimestamp(segment[1], `activeSegments[${index}][1]`),
  ]);
  if (!segments.length || !previous ||
    event - previous >= IDLE_GAP_MINUTES * 60 * 1000) {
    segments.push([event, event]);
  } else {
    segments[segments.length - 1][1] = event;
  }
  return { activeSegments: segments.map(serialize), lastActivityAt: event.toISOString() };
}

export function resolveFinishTiming({ finishedAt, lastActivityAt } = {}) {
  const finish = parseTimestamp(finishedAt, "finishedAt");
  const last = parseTimestamp(lastActivityAt, "lastActivityAt");
  const stale = finish - last >= IDLE_GAP_MINUTES * 60 * 1000;
  return {
    finishedAt: finish.toISOString(),
    workoutEndedAt: (stale ? last : finish).toISOString(),
    extendActivityToFinish: !stale,
  };
}
